/**
 * Utility to check if the backend server is running and accessible
 */

import axios from 'axios';
import { getApiUrl } from './apiUrl';

/**
 * Check if the server is running by testing the /settings endpoint
 * @returns {Promise<{isRunning: boolean, status: number, data: any, error: string}>}
 */
export const checkServerStatus = async () => {
  try {
    const apiUrl = getApiUrl();
    console.log('🔍 Checking server at:', apiUrl);
    
    const response = await axios.get(`${apiUrl}/settings`, {
      timeout: 5000 // 5 second timeout
    });
    
    return {
      isRunning: true,
      status: response.status,
      data: response.data,
      message: '✅ Server is running and responding!'
    };
  } catch (error) {
    let message = '❌ Server is not responding';
    
    if (error.code === 'ECONNREFUSED') {
      message = '❌ Connection refused - Server is not running or not accessible';
    } else if (error.code === 'ETIMEDOUT' || error.message.includes('timeout')) {
      message = '❌ Request timeout - Server may be slow or unreachable';
    } else if (error.response) {
      // Server responded but with an error status
      message = `⚠️ Server responded with status ${error.response.status}`;
      return {
        isRunning: true, // Server is running, just returning an error
        status: error.response.status,
        data: error.response.data,
        message
      };
    } else if (error.request) {
      message = '❌ No response from server - Server may be down';
    }
    
    return {
      isRunning: false,
      status: null,
      data: null,
      error: error.message,
      message
    };
  }
};

/**
 * Test multiple endpoints to verify server functionality
 * @returns {Promise<Object>}
 */
export const testServerEndpoints = async () => {
  const apiUrl = getApiUrl();
  const results = {
    baseUrl: apiUrl,
    timestamp: new Date().toISOString(),
    endpoints: {}
  };
  
  const endpoints = [
    { name: 'Settings', path: '/settings' },
    { name: 'Products', path: '/products?limit=1' },
    { name: 'Categories', path: '/products/categories/all' }
  ];
  
  for (const endpoint of endpoints) {
    try {
      const response = await axios.get(`${apiUrl}${endpoint.path}`, {
        timeout: 5000
      });
      results.endpoints[endpoint.name] = {
        status: '✅ OK',
        statusCode: response.status,
        hasData: !!response.data
      };
    } catch (error) {
      results.endpoints[endpoint.name] = {
        status: '❌ ERROR',
        statusCode: error.response?.status || 'N/A',
        error: error.message
      };
    }
  }
  
  return results;
};

/**
 * Log server status to console (useful for debugging)
 */
export const logServerStatus = async () => {
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🔍 SERVER STATUS CHECK');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  const status = await checkServerStatus();
  console.log('API URL:', getApiUrl());
  console.log('Status:', status.message);
  
  if (status.isRunning) {
    console.log('Status Code:', status.status);
    if (status.data) {
      console.log('Response Data:', status.data);
    }
  } else {
    console.log('Error:', status.error);
    console.log('');
    console.log('💡 Troubleshooting:');
    console.log('1. Check if your Node.js server is running');
    console.log('2. Verify the API URL is correct');
    console.log('3. Check firewall/network settings');
    console.log('4. Ensure the server is listening on the correct port');
  }
  
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  return status;
};
